import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { AppLocale } from '@/i18n/config';
import { formatAmountMinor, formatCompetenceMonth } from '../finance-format';
import { MOVEMENT_COLUMN_KEYS, type FinanceMovement } from './dashboard-model';

type MovementsCardProps = {
  movements: FinanceMovement[];
  selectedYear: number;
  refreshing: boolean;
  hasActiveFilter: boolean;
  canWriteForecast: boolean;
  locale: AppLocale;
  onEditForecast: (movement: FinanceMovement) => void;
};

type MovementRowProps = {
  movement: FinanceMovement;
  canEdit: boolean;
  locale: AppLocale;
  onEditForecast: (movement: FinanceMovement) => void;
};

function MovementRow({ movement, canEdit, locale, onEditForecast }: MovementRowProps) {
  const { t } = useTranslation('finance');

  return (
    <tr className="border-border border-t">
      <td className="py-2 pr-3">{formatCompetenceMonth(movement.competenceMonth, locale)}</td>
      <td className="py-2 pr-3">
        <div className="flex flex-wrap items-center gap-2">
          <span>{movement.description}</span>
          {canEdit && movement.origin === 'forecast-manual' && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onEditForecast(movement)}
            >
              {t('dashboard.forecast.edit', {
                description: movement.description,
              })}
            </Button>
          )}
        </div>
      </td>
      <td className="py-2 pr-3">{movement.category}</td>
      <td className="py-2 pr-3">{t(`dashboard.directionValues.${movement.direction}`)}</td>
      <td className="py-2 pr-3">{movement.movementStatus}</td>
      <td className="py-2 pr-3">
        {/*
          Named "Provenance", not "Origin": the card above this table is already
          called "Data origin", and it answers a different question — where the
          DATASET came from, not where this row did.
        */}
        {t(`dashboard.originValues.${movement.origin}`)}
      </td>
      <td className="py-2 pr-3">
        {/*
          Rendered, not merely filterable. Exposing the label as a filter while never
          showing it would let a reader narrow by a value they cannot see.
        */}
        {movement.projectLabel ?? t('dashboard.table.noProjectLabel')}
      </td>
      <td className="py-2 pr-3">
        {/*
          Spelled out rather than left blank when false. A blank cell in a boolean
          column reads as missing data, and this flag decides whether the amount
          counts economically.
        */}
        {movement.economicRelevant
          ? t('dashboard.economicRelevantValues.yes')
          : t('dashboard.economicRelevantValues.no')}
      </td>
      <td className="py-2">{formatAmountMinor(movement.amountMinor, locale)}</td>
    </tr>
  );
}

function EmptyMovementsRow({ hasActiveFilter }: { hasActiveFilter: boolean }) {
  const { t } = useTranslation('finance');

  return (
    <tr>
      <td colSpan={MOVEMENT_COLUMN_KEYS.length} className="py-4 text-center text-foreground/60">
        {/*
          The server narrowed these rows, so an empty list no longer tells the page
          which of the two situations it is in. The page knows anyway — it sent the
          filters — and the discrimination costs no contract field. Without it, a year
          holding 674 movements reads as empty.
        */}
        {hasActiveFilter ? t('dashboard.filteredEmpty') : t('dashboard.empty')}
      </td>
    </tr>
  );
}

export function MovementsCard(props: MovementsCardProps) {
  const { t } = useTranslation('finance');
  const { movements, refreshing } = props;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.movementsTitle', { year: props.selectedYear })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/*
          `aria-busy` alone is not feedback: it produces nothing at all for the sighted reader this
          demo is built for. The visible line is the actual signal; the attribute is what makes the
          same fact reach assistive technology.
        */}
        {refreshing && (
          <p className="text-sm text-foreground/60" role="status">
            {t('dashboard.refreshing')}
          </p>
        )}
        <div className="overflow-x-auto" aria-busy={refreshing}>
          <table className="w-full text-sm" aria-label={t('dashboard.movementsTableAria')}>
            <thead>
              <tr className="border-border border-b text-left">
                {MOVEMENT_COLUMN_KEYS.map((key, index) => (
                  <th
                    key={key}
                    className={index === MOVEMENT_COLUMN_KEYS.length - 1 ? 'py-2' : 'py-2 pr-3'}
                  >
                    {t(`dashboard.table.${key}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {movements.length === 0 ? (
                <EmptyMovementsRow hasActiveFilter={props.hasActiveFilter} />
              ) : (
                movements.map((movement) => (
                  <MovementRow
                    key={movement.id}
                    movement={movement}
                    canEdit={props.canWriteForecast}
                    locale={props.locale}
                    onEditForecast={props.onEditForecast}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
